import React, { useState, useRef, useEffect } from 'react';
import { User } from '../../services/authService';
import { NewsService, NewsSummary, NewsSummaryListItem, NEWS_SOURCES, NewsSource } from '../../services/newsService';
import PageHeader from '../common/PageHeader';
import './News.css';

interface NewsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type LoadingStage = 'idle' | 'searching' | 'summarizing';
type ViewMode = 'current' | 'past-list' | 'past-summary';

const News: React.FC<NewsProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const [currentSummary, setCurrentSummary] = useState<NewsSummary | null>(null);
  const [selectedPastSummary, setSelectedPastSummary] = useState<NewsSummary | null>(null);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [keywords, setKeywords] = useState('');
  const [pastSummaries, setPastSummaries] = useState<NewsSummaryListItem[]>([]);
  const [loadingPastSummaries, setLoadingPastSummaries] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load latest summary on mount
  useEffect(() => {
    loadLatestSummary();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const loadLatestSummary = async () => {
    try {
      const result = await NewsService.getPastSummaries(1);
      if (result.summaries.length > 0) {
        const latestSummary = await NewsService.getSummaryById(result.summaries[0].lesson_id);
        setCurrentSummary(latestSummary);
      }
    } catch (err) {
      console.error('Error loading latest summary:', err);
      // It's okay if there are no summaries yet
    }
  };

  const handleOpenGenerate = () => {
    setShowMenu(false);
    setSelectedSources([]);
    setKeywords('');
    setShowGenerateModal(true);
  };

  const handleToggleSource = (sourceId: string) => {
    setSelectedSources(prev =>
      prev.includes(sourceId)
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleGenerate = async () => {
    if (selectedSources.length === 0) {
      setError('Please select at least one news source');
      return;
    }

    try {
      setShowGenerateModal(false);
      setLoadingStage('searching');
      setError(null);

      const newSummary = await NewsService.generateNewsSummary(
        selectedSources,
        keywords.trim() || undefined,
        (stage) => {
          setLoadingStage(stage);
        }
      );

      setCurrentSummary(newSummary);
      setLoadingStage('idle');
      setViewMode('current');
    } catch (err) {
      console.error('Error generating news summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate news summary. Please try again.');
      setLoadingStage('idle');
    }
  };

  const handleOpenPastSummaries = async () => {
    setShowMenu(false);
    setViewMode('past-list');
    setLoadingPastSummaries(true);
    try {
      const result = await NewsService.getPastSummaries(100);
      setPastSummaries(result.summaries);
    } catch (err) {
      console.error('Error loading past summaries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load past summaries');
    } finally {
      setLoadingPastSummaries(false);
    }
  };

  const handleSelectPastSummary = async (summaryId: string) => {
    try {
      const summary = await NewsService.getSummaryById(summaryId);
      setSelectedPastSummary(summary);
      setViewMode('past-summary');
    } catch (err) {
      console.error('Error loading summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to load summary');
    }
  };

  const handleBackToCurrentSummary = () => {
    setViewMode('current');
    setSelectedPastSummary(null);
    setSearchQuery('');
  };

  const handleDeleteClick = (summaryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm(summaryId);
  };

  const handleConfirmDelete = async (summaryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await NewsService.deleteSummary(summaryId);
      setPastSummaries(pastSummaries.filter(s => s.lesson_id !== summaryId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete summary');
    }
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getCurrentSummary = () => {
    if (viewMode === 'past-summary' && selectedPastSummary) {
      return selectedPastSummary;
    }
    return currentSummary;
  };

  const filteredSummaries = pastSummaries.filter(summary =>
    summary.lesson_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    summary.lesson_objective.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const showBackButton = viewMode === 'past-summary' || viewMode === 'past-list';
  const summary = getCurrentSummary();

  return (
    <div className="news-container">
      {/* Page Header */}
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          showBackButton ? (
            <button
              className="page-header-icon-btn news-back-btn"
              onClick={handleBackToCurrentSummary}
              aria-label="Back to current summary"
            >
              ←
            </button>
          ) : undefined
        }
        centerContent={
          <h1 className="page-header-center-title">
            {viewMode === 'past-list' ? 'Past News' : viewMode === 'past-summary' ? 'Past Summary' : 'Vietnamese News'}
          </h1>
        }
        rightActions={
          viewMode !== 'past-list' ? (
            <div className="news-menu-container" ref={menuRef}>
              <button
                className="page-header-icon-btn"
                onClick={() => setShowMenu(!showMenu)}
                disabled={loadingStage !== 'idle'}
              >
                ⋮
              </button>
              {showMenu && (
                <div className="news-menu-dropdown">
                  <button
                    className="news-menu-item"
                    onClick={handleOpenGenerate}
                  >
                    ✨ Generate News Summary
                  </button>
                  <button
                    className="news-menu-item"
                    onClick={handleOpenPastSummaries}
                  >
                    📚 Past Summaries
                  </button>
                </div>
              )}
            </div>
          ) : undefined
        }
      />

      {/* Main Content */}
      <div className="news-main-content">
        {/* Loading State */}
        {loadingStage !== 'idle' && (
          <div className="news-loading">
            <div className="news-spinner-large"></div>
            <p className="news-loading-text">
              {loadingStage === 'searching' && 'Searching Vietnamese news sources...'}
              {loadingStage === 'summarizing' && 'Generating summary...'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && loadingStage === 'idle' && (
          <div className="news-error">
            <p>{error}</p>
            <button
              className="news-retry-btn"
              onClick={() => setError(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Past Summaries List View */}
        {viewMode === 'past-list' && loadingStage === 'idle' && !error && (
          <div className="news-past-list">
            <div className="news-past-list-search">
              <input
                type="text"
                className="news-search-input"
                placeholder="Search summaries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {loadingPastSummaries && (
              <div className="news-loading">
                <div className="news-spinner"></div>
                <p>Loading summaries...</p>
              </div>
            )}

            {!loadingPastSummaries && filteredSummaries.length === 0 && searchQuery && (
              <div className="news-empty">
                <p>No summaries found matching "{searchQuery}"</p>
              </div>
            )}

            {!loadingPastSummaries && pastSummaries.length === 0 && !searchQuery && (
              <div className="news-empty">
                <p>No past summaries yet. Generate your first summary to get started!</p>
              </div>
            )}

            {!loadingPastSummaries && filteredSummaries.length > 0 && (
              <div className="news-past-list-items">
                {filteredSummaries.map((item) => (
                  <div
                    key={item.lesson_id}
                    className="news-past-item"
                    onClick={() => handleSelectPastSummary(item.lesson_id)}
                  >
                    <div className="news-past-item-content">
                      <div className="news-past-item-title">{item.lesson_name}</div>
                      <div className="news-past-item-description">
                        {truncateText(item.lesson_objective, 120)}
                      </div>
                      <div className="news-past-item-date">
                        {formatDate(item.created_at)}
                      </div>
                    </div>
                    <div className="news-past-item-actions">
                      {deleteConfirm === item.lesson_id ? (
                        <div className="news-delete-confirm">
                          <button
                            className="news-confirm-btn"
                            onClick={(e) => handleConfirmDelete(item.lesson_id, e)}
                          >
                            Delete
                          </button>
                          <button
                            className="news-cancel-btn"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="news-delete-btn"
                          onClick={(e) => handleDeleteClick(item.lesson_id, e)}
                          aria-label="Delete summary"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary Content View */}
        {(viewMode === 'current' || viewMode === 'past-summary') && summary && loadingStage === 'idle' && (
          <div className="news-summary-view">
            <h1 className="news-title">{summary.lesson_name}</h1>

            {summary.lesson_content.source && (
              <div className="news-source-badge-container">
                <span className={`news-source-badge news-source-${NewsService.getSourceId(summary.lesson_content.source)}`}>
                  {NewsService.getSourceName(summary.lesson_content.source)}
                </span>
                {summary.lesson_content.publishedDate && (
                  <span className="news-published-date">
                    {formatDate(summary.lesson_content.publishedDate)}
                  </span>
                )}
              </div>
            )}

            {summary.lesson_content.keyPoints && summary.lesson_content.keyPoints.length > 0 && (
              <div className="news-key-points">
                <h3 className="news-section-title">Key Points</h3>
                <ul className="news-key-points-list">
                  {summary.lesson_content.keyPoints.map((point, index) => (
                    <li key={index} className="news-key-point">
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="news-summary-content">
              <h3 className="news-section-title">Summary</h3>
              <div className="news-summary-text">
                {summary.lesson_content.summary}
              </div>
            </div>

            {summary.lesson_content.sourceUrl && (
              <div className="news-source-link">
                <a
                  href={summary.lesson_content.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="news-source-link-btn"
                >
                  View Original Article →
                </a>
              </div>
            )}
          </div>
        )}

        {/* Welcome Message */}
        {viewMode === 'current' && !summary && loadingStage === 'idle' && !error && (
          <div className="news-welcome">
            <h2>Welcome to Vietnamese News Summaries</h2>
            <p>Get AI-powered summaries of recent Vietnamese news from trusted sources.</p>
            <div className="news-features">
              <div className="news-feature">
                <span className="news-feature-icon">🔍</span>
                <span>Real-time news search</span>
              </div>
              <div className="news-feature">
                <span className="news-feature-icon">📰</span>
                <span>Multiple trusted sources</span>
              </div>
              <div className="news-feature">
                <span className="news-feature-icon">🤖</span>
                <span>AI-powered summaries</span>
              </div>
            </div>
            <button
              className="news-welcome-btn"
              onClick={handleOpenGenerate}
            >
              Generate News Summary
            </button>
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <>
          <div
            className="news-modal-overlay"
            onClick={() => setShowGenerateModal(false)}
          />
          <div className="news-modal">
            <div className="news-modal-header">
              <h2 className="news-modal-title">Generate News Summary</h2>
              <button
                className="news-modal-close"
                onClick={() => setShowGenerateModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="news-modal-content">
              <div className="news-modal-section">
                <label className="news-modal-label">Select News Sources</label>
                <div className="news-sources-grid">
                  {NEWS_SOURCES.map((source) => (
                    <label key={source.id} className="news-source-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedSources.includes(source.id)}
                        onChange={() => handleToggleSource(source.id)}
                      />
                      <span className="news-source-checkbox-label">{source.name}</span>
                    </label>
                  ))}
                </div>
                {selectedSources.length === 0 && (
                  <p className="news-modal-hint error">Please select at least one source</p>
                )}
              </div>

              <div className="news-modal-section">
                <label htmlFor="keywords" className="news-modal-label">
                  Keywords (Optional)
                </label>
                <input
                  id="keywords"
                  type="text"
                  className="news-modal-input"
                  placeholder="e.g., technology, sports, economy..."
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                />
                <p className="news-modal-hint">
                  Leave empty for general news, or specify topics to focus on.
                </p>
              </div>
            </div>

            <div className="news-modal-footer">
              <button
                className="news-modal-btn news-modal-btn-cancel"
                onClick={() => setShowGenerateModal(false)}
              >
                Cancel
              </button>
              <button
                className="news-modal-btn news-modal-btn-generate"
                onClick={handleGenerate}
                disabled={selectedSources.length === 0}
              >
                Generate Summary
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default News;

